(ns app.front.view.group.reporting.permit-package
  (:require [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.table :as table]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :as event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema-tools.core :as st]
            [schema.core :as s]))

(def visible-submission-states #{:permit-package-submission.integration-state/failed
                                 :permit-package-submission.integration-state/complete})

(def fetch-amount 20)

(defn- fetch-single-state
  [{:keys [db]} {:keys [group-id state opts]}]
  {:fx [[:comm/command {:command          :reports/group-package-submissions
                        :data             (merge {:group-id group-id
                                                  :states   #{state}
                                                  :from     0
                                                  :limit    fetch-amount}
                                                 opts)
                        :on-success       [::fetch-submissions.success state]
                        :finally-dispatch [::fetch-submissions.done state]}]]
   :db (assoc-in db [::data state :loading?] true)})

(rf/reg-event-fx ::fetch-submissions-for-state
  (fn [cofx [_ {:keys [group-id state opts]}]]
    (fetch-single-state cofx (util/mmap group-id state opts))))

(defn- clear-state-on-group-change
  "Keep track of which group we're looking at and reset if the group changes"
  [group-id {:keys [db]}]
  (let [old-group (get-in db [::data :group-id])]
    {:db (cond-> db
           (not= old-group group-id) (dissoc ::data)
           true                      (assoc-in [::data :group-id] group-id))}))

(defn- fetch-visible-submissions
  [group-id cofx]
  (event/merge-every-item
    cofx
    (fn [cofx state] (fetch-single-state cofx (util/mmap group-id state)))
    visible-submission-states))

(rf/reg-event-fx ::show-permit-package-submissions-for-group
  (fn [cofx [_ group-id]]
    (event/merge-every-fn
      cofx
      (partial clear-state-on-group-change group-id)
      (partial fetch-visible-submissions group-id))))

(rf/reg-event-fx ::fetch-submissions.success
  (fn [{:keys [db]} [_ state status {:keys [submissions messages total]}]]
    {:db       (-> db
                   (assoc-in [::data state :total] total)
                   (update-in [::data :messages] merge messages))
     :dispatch [::entities/merge-entity-db status (du/entities->entity-db submissions)]}))

(rf/reg-event-fx ::fetch-submissions.done
  (fn [{:keys [db]} [_ state]]
    {:db (assoc-in db [::data state :loading?] false)}))

(rf/reg-event-fx ::delete-submissions
  (fn [_ [_ state ids]]
    {:comm/command {:command    :reports/delete-package-submissions
                    :data       {:submission-ids ids}
                    :on-success [::delete-submissions.success state (- (count ids))]}}))

(rf/reg-event-fx ::delete-submissions.success
  (fn [{:keys [db]} [_ state diff status db-changes]]
    {:db (update-in db [::data state :total] + diff)
     :fx [[:dispatch [::entities/apply-changes status db-changes]]]}))

(rf/reg-event-fx ::retry-submissions
  (fn [_ [_ state ids]]
    {:comm/command {:command    :reports/retry-package-submissions
                    :data       {:submission-ids ids}
                    :on-success [::retry-submissions.success state (- (count ids))]}}))

(rf/reg-event-fx ::retry-submissions.success
  (fn [{:keys [db]} [_ state diff status db-changes]]
    {:db (update-in db [::data state :total] + diff)
     :fx [[:dispatch [::entities/apply-changes status db-changes]]]}))

(defn- refresh-state
  [{:keys [db]} {:keys [group-id state]}]
  (when (and (some? group-id)
             (= group-id (get-in db [::data :group-id])))
    ;; When retrying submissions, add a bit of debounce so that we won't try to invoke
    ;; the backend for each message separately.
    ;; Note that :app.front.notifications/backend-notifications-loaded also dedupes
    ;; the :notification/re-frame-event events if the backend provides several notifications at once.
    {:fx [[:dispatch-debounce [{:id      [::refresh-state state]
                                :timeout 1000
                                :action  :dispatch
                                :event   [::fetch-submissions-for-state
                                          (util/mmap group-id state)]}]]]}))

;; Invoked through the notification callback
(rf/reg-event-fx :permit-package-submission/on-submit.success
  (fn [cofx [_ group-id]]
    (refresh-state cofx {:state    :permit-package-submission.integration-state/complete
                         :group-id group-id})))

;; Invoked through the notification callback
(rf/reg-event-fx :permit-package-submission/on-submit.failure
  (fn [cofx [_ group-id]]
    (refresh-state cofx {:state    :permit-package-submission.integration-state/failed
                         :group-id group-id})))

(rf/reg-sub ::data
  :-> ::data)

(rf/reg-sub ::data-for-state
  :<- [::data]
  (fn [data [_ state]]
    {:pre [(keyword? state)]}
    (get data state)))

(rf/reg-sub ::total
  (fn [[_ state]]
    (rf/subscribe [::data-for-state state]))
  (fn [{:keys [total]}]
    (or total 0)))

(rf/reg-sub ::loading?
  (fn [[_ state]]
    (rf/subscribe [::data-for-state state]))
  (fn [{:keys [loading? total]}]
    (boolean (and loading?
                  (nil? total)))))

(rf/reg-sub ::messages
  :<- [::data]
  (fn [{:keys [messages]}]
    messages))

(rf/reg-sub ::message
  :<- [::messages]
  (fn [messages [_ submission-id]]
    {:pre [(int? submission-id)]}
    (get messages submission-id)))

(rf/reg-sub ::tz
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entity group-id]))
  (fn [group]
    (-> group :group/tz)))

(rf/reg-sub ::submissions
  (fn [[_ group-id state]]
    {:pre [(int? group-id)
           (keyword? state)]}
    (rf/subscribe [::entities/entities
                   {:entity/type                                 :entity.type/permit-package-submission
                    :entity/parent                               group-id
                    :permit-package-submission/integration-state state}]))
  (fn [submissions]
    (->> submissions
         (sort-by
           (some-fn :permit-package-submission/integration-response-time
                    :entity/created)
           >))))

(rf/reg-sub ::submission-count
  (fn [[_ group-id state]]
    {:pre [(int? group-id)
           (keyword? state)]}
    (rf/subscribe [::submissions group-id state]))
  :-> count)

(defn- delete-button
  [{:keys [tr *selected-submissions on-delete]}]
  (let [selected-count (count @*selected-submissions)]
    [ui/modal-confirmation-button
     {:id                  "delete-submissions"
      :disabled?           (zero? selected-count)
      :trigger-button-opts {:kind :secondary
                            :icon i/trash}
      :tr                  tr
      :trigger-button-text (tr [:permit-package-submission/delete] [selected-count])
      :on-yes-click        (fn [_]
                             (on-delete @*selected-submissions)
                             (reset! *selected-submissions #{}))}
     [:div (tr [:permit-package-submission/delete-confirm]
               [selected-count])]]))

(defn- retry-button
  [{:keys [tr *selected-submissions on-retry]}]
  (let [selected-count (count @*selected-submissions)]
    (cc/button {:icon     i/upload
                :kind     :primary
                :disabled (zero? selected-count)
                :on-click (fn [_]
                            (on-retry @*selected-submissions)
                            (reset! *selected-submissions #{}))}
               (tr [:permit-package-submission/retry]
                   [selected-count]))))

(defn- load-more-button
  [{:keys [tr on-load-more remaining]}]
  [:div.bx--row>div.bx--col-xs-12.flex.justify-center
   [:div.cursor-pointer.p-4.flex.flex-col.items-center
    {:on-click on-load-more}
    (cc/icon {:icon        i/refresh
              :class-name  "mb-4"
              :description (tr [:dashboard-search/load-more] [remaining])})
    [:span.semibold (tr [:dashboard-search/load-more] [remaining])]]])

(defn- render-time
  [tz {:permit-package-submission/keys [integration-response-time]
       :entity/keys                    [created]}]
  (date-util/date-time-in-tz (or integration-response-time created) tz))

(defn- render-year
  [{:keys [get-message]} {:db/keys [id]}]
  (let [message @(get-message id)]
    (:permit-package-message/approval-year message)))

(defn- render-checkbox
  [{:keys [tr enable-selection? *selected-submissions]} {:db/keys [id]}]
  (when enable-selection?
    (let [large-screen? (<sub [::media-query/breakpoint-matches? :lg])]
      (cc/checkbox {:id         (str id "-select")
                    :label-text (tr [:select/select])
                    :hide-label large-screen?
                    :checked    (contains? @*selected-submissions id)
                    :on-change  #(swap! *selected-submissions (if % conj disj) id)}))))

(defn- render-permit-type
  [{:keys [tr get-message]} {:db/keys [id]}]
  (let [message @(get-message id)]
    (cc/link
      {:href (href/href :view/workspace {:workspace-id (:workspace/id message)})}
      (case (:workspace/category message)
        :category/plans-resubmission (tr [:permit-package-submission/type-resubmission])
        ;; else
        (tr [:permit-package-submission/type-permit])))))

(defn- render-permit-number
  [{:keys [get-message]} {:db/keys [id]}]
  (let [message @(get-message id)]
    (:permit-package-message/permit-number message)))

(defn- render-roll-number
  [{:keys [get-message]} {:db/keys [id]}]
  (let [message @(get-message id)]
    (:permit-package-message/roll-number message)))

(defn- render-filename
  [{:keys [get-message]} {:db/keys [id]}]
  (let [message @(get-message id)]
    (:permit-package-message/filename message)))

(defn- render-download
  [{:keys [tr get-message]} {:db/keys [id]}]
  (let [{:keys [verdict-id]} @(get-message id)]
    (cc/button
      {:kind             :ghost
       :size             :small
       :icon             i/download
       :icon-description (tr [:download])
       :href             (str "/api/command/verdict/download?id=" verdict-id)
       :data-cy          "download-permit-btn"}
      (tr [:download]))))

(defn- parse-error
  [integration-error]
  (try
    ;; Errors from MPAC should always be JSON, but errors
    ;; originating from siirappi itself might not be
    (let [json-obj  (.parse js/JSON integration-error)
          json-text (.stringify js/JSON json-obj nil " ")]
      json-text)
    (catch js/Error _
      integration-error)))

(defn- render-error
  [{:keys [tr]} {:permit-package-submission/keys [integration-error]}]
  [:div.bg-ui-light-gray.p-2
   (if (some? integration-error)
     (let [error (parse-error integration-error)]
       [:div.mb-2
        [:strong (tr [:permit-package-submission/integration-error])]
        [:pre.font-mono error]])
     (tr [:permit-package-submission/invalid-message]))])

(rf/reg-sub ::show-all-cols?
  :<- [::media-query/currently-matching-breakpoints]
  (fn [breakpoints]
    (or (contains? breakpoints :xxl)
        (not (contains? breakpoints :lg)))))

(defn table
  [{:keys [tr tz *selected-submissions get-message get-submissions enable-selection? expandable?]}]
  (let [submissions    @(get-submissions)
        show-all-cols? (<sub [::show-all-cols?])]
    [table/modern-responsive-table
     (merge {:cols  [[""
                      (partial render-checkbox (util/mmap tr enable-selection? *selected-submissions))
                      {:max-width "35px"}]
                     [:permit-package-submission/time
                      (partial render-time tz)
                      {:min-width "110px"}]
                     [:permit-package-submission/type
                      (partial render-permit-type (util/mmap tr get-message))
                      {:min-width "150px"}]
                     [:permit-package-submission/permit-number
                      (partial render-permit-number (util/mmap get-message))]
                     [:permit-package-submission/roll-number
                      (partial render-roll-number (util/mmap get-message))
                      {:min-width "180px"}]
                     (when show-all-cols?
                       [:permit-package-submission/approval-year
                        (partial render-year (util/mmap get-message))
                        {:max-width "70px"}])
                     [:permit-package-submission/filename
                      (partial render-filename (util/mmap get-message))
                      {:min-width "300px"}]
                     (when show-all-cols?
                       [:verdict/download
                        (partial render-download (util/mmap get-message tr))
                        {:max-width "170px"}])]
             :items submissions
             :tr    tr}
            (when expandable?
              {:expanded-func (partial render-error (util/mmap tr))}))]))

(s/defschema SubmissionTableContext
  (st/merge
    {:tr               (s/pred fn?)
     :tz               s/Str
     :title-key        s/Keyword
     :loading?         s/Bool
     :total            s/Int
     :submission-count s/Int
     :get-message      (s/pred fn?)
     :get-submissions  (s/pred fn?)
     :on-load-more     (s/pred fn?)}
    (st/optional-keys
      {:expandable? s/Bool
       :on-delete   (s/pred fn?)
       :on-retry    (s/pred fn?)})))

(s/defn submission-table [{:keys [tr title-key submission-count total expandable?
                                  loading? get-submissions get-message
                                  on-delete on-retry on-load-more]} :- SubmissionTableContext]
  (r/with-let [*selected-submissions (r/atom #{})]
    (let [enable-selection? (or (some? on-delete)
                                (some? on-retry))
          total             (max total submission-count)
          remaining         (- total submission-count)]
      (if loading?
        (cc/data-table-skeleton {:column-count 5})
        [:div.mt-8.space-y-8
         [:div.flex.flex-col.sm:flex-row.flex-1.justify-between.items-center
          [:h3.mb-0 (tr [title-key] [total])]
          [:div.grid.grid-cols-1.sm:grid-cols-2.gap-2
           (when on-delete
             [delete-button (util/mmap tr *selected-submissions on-delete)])
           (when on-retry
             [retry-button (util/mmap tr *selected-submissions on-retry)])]]
         (if (zero? submission-count)
           [:div (tr [:permit-package-submission/no-submissions])]
           [table (util/mmap tr *selected-submissions get-submissions get-message enable-selection? expandable?)])
         (when (and on-load-more (pos? remaining))
           [load-more-button (util/mmap tr remaining on-load-more)])]))))

(defn with-state-params
  [ctx group-id state]
  (let [submission-count (<sub [::submission-count group-id state])]
    (merge ctx
           {:loading?         (<sub [::loading? state])
            :total            (<sub [::total state])
            :get-submissions  #(rf/subscribe [::submissions group-id state])
            :get-message      #(rf/subscribe [::message %])
            :submission-count submission-count
            :on-load-more     #(>evt [::fetch-submissions-for-state
                                      (util/mmap
                                        {:opts {:from submission-count}}
                                        group-id state)])})))

(defn submissions-view
  [group-id]
  (let [ctx {:tr (<sub [:tr])
             :tz (<sub [::tz group-id])}]
    [:<>
     (let [state :permit-package-submission.integration-state/failed]
       [submission-table
        (-> ctx
            (with-state-params group-id state)
            (assoc :title-key :permit-package-submission/failed
                   :expandable? true
                   :on-delete #(>evt [::delete-submissions state %])
                   :on-retry #(>evt [::retry-submissions state %])))])
     [submission-table
      (-> ctx
          (with-state-params group-id :permit-package-submission.integration-state/complete)
          (assoc :title-key :permit-package-submission/success))]]))
